import React from 'react';
import {
  CallToAction,
  ClosingBand,
  FaqList,
  FeaturePanel,
  Hero,
  Highlight,
  NumberedSteps,
  PlayCard,
  ResearchCards,
  ScrollLink,
  Section,
  SiteFooter,
  SitePage,
  Statement,
  StoryRows,
  Wordmark
} from '@talbenmoshe/naru-parent-design-system';
import { useMessages } from './i18n/messagesContext.js';
import { LANDING_PICTURES } from './landingPictures.js';
import { useDashboardAppUrl } from './SiteLinksContext.js';

const HOME_ADDRESS = '/';
const LESSONS_SECTION_ID = 'lessons';
// Miki's public demo of Mason's basketball lesson: the only public lesson today.
const MASON_DEMO_LESSON_ADDRESS = 'https://naru-lessons.vercel.app/?l=ql12s2u#n=Mason';

/** The landing page at site.talbenmoshe.com, in the shape of trynaru.com/lessons; every call to action opens the dashboard. */
export function LandingPage() {
  const messages = useMessages();
  const dashboardAppUrl = useDashboardAppUrl();

  return (
    <SitePage footer={<SiteFooter>{messages.LANDING_FOOTER()}</SiteFooter>}>
      <Hero
        masthead={<Wordmark label={messages.LOGO_LABEL()} href={HOME_ADDRESS} />}
        titleLines={[
          messages.LANDING_HERO_TITLE_FIRST_LINE(),
          <>
            {messages.LANDING_HERO_TITLE_LEAD()} <Highlight>{messages.LANDING_HERO_TITLE_HIGHLIGHT()}</Highlight>
            {messages.LANDING_HERO_TITLE_END()}
          </>
        ]}
        subtitleLines={[messages.LANDING_HERO_SUBTITLE_FIRST_LINE(), messages.LANDING_HERO_SUBTITLE_SECOND_LINE()]}
        actions={
          <>
            <CallToAction label={messages.LANDING_CALL_TO_ACTION()} href={dashboardAppUrl} note={messages.LANDING_CALL_TO_ACTION_NOTE()} />
            <ScrollLink label={messages.LANDING_SEE_LESSON()} targetId={LESSONS_SECTION_ID} />
          </>
        }
        photo={{ wideSrc: LANDING_PICTURES.heroWide, tallSrc: LANDING_PICTURES.heroTall, description: messages.LANDING_PICTURE_HERO() }}
        cards={[
          { src: LANDING_PICTURES.basketballQuestion, description: messages.LANDING_PICTURE_BASKETBALL_QUESTION() },
          { src: LANDING_PICTURES.bananaPeel, description: messages.LANDING_PICTURE_BANANA_PEEL(), focus: { x: 50, y: 30 } },
          { src: LANDING_PICTURES.arena, description: messages.LANDING_PICTURE_ARENA(), focus: { x: 50, y: 20 } }
        ]}
      />
      <Section id="intro">
        <Statement lines={[messages.LANDING_STATEMENT_FIRST_LINE(), messages.LANDING_STATEMENT_SECOND_LINE()]} />
      </Section>
      <Section id="how" surface="surface" title={messages.LANDING_HOW_TITLE()}>
        <NumberedSteps
          steps={[
            {
              picture: { src: LANDING_PICTURES.steps[0], description: '' },
              title: messages.LANDING_STEP_1_TITLE(),
              lines: [messages.LANDING_STEP_1_LINE_1(), messages.LANDING_STEP_1_LINE_2()]
            },
            {
              picture: { src: LANDING_PICTURES.steps[1], description: '' },
              title: messages.LANDING_STEP_2_TITLE(),
              lines: [messages.LANDING_STEP_2_LINE_1(), messages.LANDING_STEP_2_LINE_2(), messages.LANDING_STEP_2_LINE_3()]
            },
            {
              picture: { src: LANDING_PICTURES.steps[2], description: '' },
              title: messages.LANDING_STEP_3_TITLE(),
              lines: [messages.LANDING_STEP_3_LINE_1(), messages.LANDING_STEP_3_LINE_2(), messages.LANDING_STEP_3_LINE_3()]
            }
          ]}
        />
      </Section>
      <Section id={LESSONS_SECTION_ID} surface="tint" title={messages.LANDING_LESSONS_TITLE()}>
        <StoryRows
          rows={[
            {
              picture: { src: LANDING_PICTURES.basketballQuestion, description: messages.LANDING_PICTURE_BASKETBALL_QUESTION() },
              label: messages.LANDING_STORY_MASON_LABEL(),
              story: messages.LANDING_STORY_MASON_TEXT(),
              extra: (
                <PlayCard
                  href={MASON_DEMO_LESSON_ADDRESS}
                  picture={{ src: LANDING_PICTURES.bananaPeel, description: messages.LANDING_PICTURE_BANANA_PEEL(), focus: { x: 50, y: 30 } }}
                  title={messages.LANDING_PLAY_TITLE()}
                  line={messages.LANDING_PLAY_LINE()}
                  action={messages.LANDING_PLAY_ACTION()}
                />
              )
            },
            {
              picture: { src: LANDING_PICTURES.coachSam, description: messages.LANDING_PICTURE_COACH_SAM(), focus: { x: 50, y: 22 } },
              label: messages.LANDING_STORY_STUCK_LABEL(),
              story: messages.LANDING_STORY_STUCK_TEXT()
            }
          ]}
        />
        <CallToAction label={messages.LANDING_CALL_TO_ACTION()} href={dashboardAppUrl} />
      </Section>
      <FeaturePanel
        picture={{ src: LANDING_PICTURES.family, description: messages.LANDING_PICTURE_FAMILY() }}
        title={messages.LANDING_PANEL_TITLE()}
        features={[
          { title: messages.LANDING_FEATURE_WORLD_TITLE(), line: messages.LANDING_FEATURE_WORLD_LINE() },
          { title: messages.LANDING_FEATURE_HELP_TITLE(), line: messages.LANDING_FEATURE_HELP_LINE() },
          { title: messages.LANDING_FEATURE_SHORT_TITLE(), line: messages.LANDING_FEATURE_SHORT_LINE() },
          { title: messages.LANDING_FEATURE_FIT_TITLE(), line: messages.LANDING_FEATURE_FIT_LINE() },
          { title: messages.LANDING_FEATURE_LOOP_TITLE(), line: messages.LANDING_FEATURE_LOOP_LINE() }
        ]}
      />
      <Section id="why" title={messages.LANDING_RESEARCH_TITLE()}>
        <ResearchCards
          cards={[
            { title: messages.LANDING_RESEARCH_INTEREST_TITLE(), line: messages.LANDING_RESEARCH_INTEREST_LINE(), citation: messages.LANDING_RESEARCH_INTEREST_CITATION() },
            { title: messages.LANDING_RESEARCH_CHALLENGE_TITLE(), line: messages.LANDING_RESEARCH_CHALLENGE_LINE(), citation: messages.LANDING_RESEARCH_CHALLENGE_CITATION() },
            { title: messages.LANDING_RESEARCH_OFTEN_TITLE(), line: messages.LANDING_RESEARCH_OFTEN_LINE(), citation: messages.LANDING_RESEARCH_OFTEN_CITATION() },
            { title: messages.LANDING_RESEARCH_CHOICE_TITLE(), line: messages.LANDING_RESEARCH_CHOICE_LINE(), citation: messages.LANDING_RESEARCH_CHOICE_CITATION() }
          ]}
          note={messages.LANDING_RESEARCH_NOTE()}
        />
      </Section>
      <Section id="faq" surface="surface" width="narrow" title={messages.LANDING_FAQ_TITLE()}>
        <FaqList
          items={[
            { question: messages.LANDING_FAQ_WHO_QUESTION(), answer: messages.LANDING_FAQ_WHO_ANSWER() },
            { question: messages.LANDING_FAQ_LENGTH_QUESTION(), answer: messages.LANDING_FAQ_LENGTH_ANSWER() },
            { question: messages.LANDING_FAQ_ALONE_QUESTION(), answer: messages.LANDING_FAQ_ALONE_ANSWER() },
            { question: messages.LANDING_FAQ_NEEDS_QUESTION(), answer: messages.LANDING_FAQ_NEEDS_ANSWER() },
            { question: messages.LANDING_FAQ_SCREEN_QUESTION(), answer: messages.LANDING_FAQ_SCREEN_ANSWER() },
            { question: messages.LANDING_FAQ_START_QUESTION(), answer: messages.LANDING_FAQ_START_ANSWER() },
            { question: messages.LANDING_FAQ_COST_QUESTION(), answer: messages.LANDING_FAQ_COST_ANSWER() }
          ]}
        />
      </Section>
      <ClosingBand
        title={messages.LANDING_BAND_TITLE()}
        action={<CallToAction label={messages.LANDING_CALL_TO_ACTION()} href={dashboardAppUrl} />}
      />
    </SitePage>
  );
}
